import { MARKETPLACE_FILTER_ORDER, MARKETPLACE_ID_PREFIX, PICKED_ITEM_NAME_SEPARATOR } from '../constants/marketplace';
import {
  IMarketplaceCartLine,
  IMarketplaceCatalogContext,
  IMarketplaceCatalogItem,
  IMarketplaceFilterOption,
  IMarketplaceFilters,
  IMarketplaceMetadata,
  IMarketplaceMetadataResponse,
  IMarketplaceProductDetails,
  IMarketplaceSelectedFilter,
  IMarketplaceVariant,
  ISupplierLock,
  IVariantOptionRow,
  MarketplaceDetailsStatusEnum,
  TMarketplaceFilterValues,
} from '../types/marketplace';
import { ICampaign, ICampaignItem } from '../types/pyg';
import { getCatalogBudget } from './credits';

export const buildCatalogContext = (
  filters: IMarketplaceFilters,
  cart: IMarketplaceCartLine[],
): IMarketplaceCatalogContext => ({
  query: filters.query.trim(),
  categories: filters.categories,
  brands: filters.brands,
  collections: filters.collections,
  budget: getCatalogBudget(filters.fitsBudget, cart),
});

const areSameValues = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((value, index) => value === b[index]);

export const isSameCatalogContext = (a: IMarketplaceCatalogContext, b: IMarketplaceCatalogContext): boolean =>
  a.query === b.query &&
  a.budget === b.budget &&
  MARKETPLACE_FILTER_ORDER.every((kind) => areSameValues(a[kind], b[kind]));

export const hasFilterValues = (filters: TMarketplaceFilterValues): boolean =>
  MARKETPLACE_FILTER_ORDER.some((kind) => filters[kind].length > 0);

export const toggleFilterValue = (values: string[], value: string): string[] =>
  values.includes(value) ? values.filter((current) => current !== value) : [...values, value];

// A value the metadata no longer lists keeps its raw value as the label so it stays removable.
export const getSelectedFilters = (
  filters: TMarketplaceFilterValues,
  metadata: IMarketplaceMetadata,
): IMarketplaceSelectedFilter[] =>
  MARKETPLACE_FILTER_ORDER.flatMap((kind) =>
    filters[kind].map((value) => ({
      kind,
      value,
      label: metadata[kind].find((option) => option.value === value)?.label ?? value,
    })),
  );

export const isMarketplaceId = (id?: string | null): boolean => !!id?.startsWith(MARKETPLACE_ID_PREFIX);

export const stripMarketplacePrefix = (id: string): string =>
  id.startsWith(MARKETPLACE_ID_PREFIX) ? id.slice(MARKETPLACE_ID_PREFIX.length) : id;

export const addMarketplacePrefix = (productId: string): string =>
  productId.startsWith(MARKETPLACE_ID_PREFIX) ? productId : `${MARKETPLACE_ID_PREFIX}${productId}`;

export const getMarketplaceItemId = (item: Pick<ICampaignItem, 'id' | 'parentId'>): string => item.parentId ?? item.id;

export const stripTrailingPeriod = (value: string): string => value.replace(/\.$/, '');

export const isSameName = (a?: string, b?: string): boolean => normalizeName(a) === normalizeName(b);

export const resolveVariantName = (productName: string, variantName?: string): string | undefined => {
  const name = variantName?.trim();

  return name && !isSameName(productName, name) ? name : undefined;
};

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

// "Cable Tidy Set - White" carries its product name, "Mugwort Tea" does not carry "Mug": the
// product name has to end at a word boundary.
const carriesProductName = (variantName: string, productName: string): boolean =>
  new RegExp(`^${escapeRegExp(normalizeName(productName))}(?![\\p{L}\\p{N}])`, 'u').test(normalizeName(variantName));

export const buildPickedItemName = (productName: string, optionName?: string): string => {
  const variantName = resolveVariantName(productName, optionName);

  if (!variantName) {
    return productName;
  }

  return carriesProductName(variantName, productName)
    ? variantName
    : `${productName}${PICKED_ITEM_NAME_SEPARATOR}${variantName}`;
};

export const getPickedMarketplaceItemName = (item: ICampaignItem, inventory: ICampaignItem[] | null): string => {
  if (!isMarketplaceId(item.parentId)) {
    return item.name;
  }
  const parent = inventory?.find((inventoryItem) => inventoryItem.id === item.parentId);

  return parent ? buildPickedItemName(parent.name, item.name) : item.name;
};

const toCredits = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) ? value : undefined;

const toOptionalString = (value: unknown): string | undefined => (value ? String(value) : undefined);

// Wire field names follow .claude/tasks/CLAIM_PAGE_MARKETPLACE.md; the store keeps its own.
export const normalizeCatalogItem = (raw: Record<string, unknown>): IMarketplaceCatalogItem | null => {
  const creditPrice = toCredits(raw.credit_price_from);

  if (typeof creditPrice !== 'number') {
    return null;
  }

  return {
    product_id: String(raw.product_id ?? ''),
    name: String(raw.title ?? ''),
    image_url: toOptionalString(raw.image_url),
    vendor: toOptionalString(raw.brand),
    supplierCode: toOptionalString(raw.supplier_code),
    supplierName: toOptionalString(raw.supplier_name),
    // Advisory only; real availability is checked when the order is placed.
    available: raw.is_out_of_stock !== true,
    creditPrice,
  };
};

const normalizeFilterOptions = (rows?: IMarketplaceFilterOption[]): IMarketplaceFilterOption[] => {
  const seen = new Set<string>();

  return (rows ?? []).reduce<IMarketplaceFilterOption[]>((options, row) => {
    const value = row?.value ? String(row.value) : '';

    if (value && !seen.has(value)) {
      seen.add(value);
      options.push({ value, label: row.label ? String(row.label) : value });
    }

    return options;
  }, []);
};

export const normalizeMetadata = (raw: IMarketplaceMetadataResponse): IMarketplaceMetadata => ({
  categories: normalizeFilterOptions(raw.categories),
  brands: normalizeFilterOptions(raw.brands),
  collections: normalizeFilterOptions(raw.collections),
});

// Variant options arrive flat; grouped so option rows and labels read them uniformly.
const toVariantOptions = (raw: Record<string, unknown>): Record<string, string> | undefined => {
  const options = {
    ...(raw.color ? { Color: String(raw.color) } : {}),
    ...(raw.size ? { Size: String(raw.size) } : {}),
  };

  return Object.keys(options).length ? options : undefined;
};

const normalizeVariant = (
  raw: Record<string, unknown>,
  drawsFromWallet: boolean,
  preselected: boolean,
): IMarketplaceVariant => {
  const inPool = raw.in_pool !== false;

  return {
    variant_id: String(raw.variant_id ?? ''),
    name: String(raw.title ?? ''),
    image_url: toOptionalString(raw.image_url),
    options: toVariantOptions(raw),
    inPool,
    drawsFromWallet,
    preselected,
    isOutOfStock: raw.is_out_of_stock === true,
    // Out of the pool the server sends 0, which must never read as free.
    creditPrice: inPool ? toCredits(raw.credit_price) : undefined,
  };
};

export const normalizeProductDetails = (raw: Record<string, unknown>): IMarketplaceProductDetails => {
  const drawsFromWallet = raw.draws_from_wallet !== false;
  const preselected = raw.preselected === true;
  const variants = Array.isArray(raw.variants)
    ? raw.variants.map((variant: Record<string, unknown>) => normalizeVariant(variant, drawsFromWallet, preselected))
    : [];
  const poolPrices = variants.filter(isVariantSelectable).map((variant) => variant.creditPrice as number);

  return {
    product_id: String(raw.product_id ?? ''),
    name: String(raw.title ?? ''),
    description: toOptionalString(raw.description),
    images: Array.isArray(raw.images) ? raw.images.map(String) : [],
    vendor: toOptionalString(raw.brand),
    supplierCode: toOptionalString(raw.supplier_code),
    supplierName: toOptionalString(raw.supplier_name),
    preselected,
    creditPrice: poolPrices.length ? Math.min(...poolPrices) : undefined,
    variants,
  };
};

const mapVariantsToSkuOptions = (variants: IMarketplaceVariant[], parent: ICampaignItem): ICampaignItem[] =>
  variants.map((variant) => {
    const { Size, size, Color, color, Flavor, flavor, ...rest } = variant.options ?? {};
    const otherOption = Object.values(rest).join(' · ');
    const initOption = (parent.sku_options ?? []).find((option) => option.id === variant.variant_id);

    return {
      id: variant.variant_id,
      internalId: variant.variant_id,
      parentId: parent.id,
      name: variant.name,
      vendor: parent.vendor,
      image_url: variant.image_url || parent.image_url,
      description: parent.description,
      size: Size ?? size,
      color: Color ?? color,
      flavor: Flavor ?? flavor,
      other_option: otherOption || undefined,
      count: isVariantPickable(variant) ? initOption?.count : 0,
      is_out_of_stock: variant.isOutOfStock,
      // IE sku_options always carry these; the shapes must match or the shared stock and
      // customization checks skip marketplace variants.
      preorder_allowed: initOption?.preorder_allowed ?? false,
      customization: {},
    };
  });

export const hydrateItemWithVariants = (item: ICampaignItem, variants: IMarketplaceVariant[]): ICampaignItem =>
  variants.filter(isVariantPickable).length > 1
    ? { ...item, sku_options: mapVariantsToSkuOptions(variants, item) }
    : item;

// Groups variant options into one row per option name, preserving arrival order. A value is
// marked out only when every variant carrying it is out of the pool.
export const buildVariantOptionRows = (variants: IMarketplaceVariant[]): IVariantOptionRow[] => {
  const rows = new Map<string, Map<string, boolean>>();

  variants.forEach((variant) => {
    Object.entries(variant.options ?? {}).forEach(([name, value]) => {
      const key = name.toLowerCase();
      const values = rows.get(key) ?? new Map<string, boolean>();

      values.set(value, (values.get(value) ?? false) || variant.preselected || variant.inPool);
      rows.set(key, values);
    });
  });

  return Array.from(rows.entries()).map(([name, values]) => ({
    name: name.charAt(0).toUpperCase() + name.slice(1),
    values: Array.from(values.entries()).map(([label, isAvailable]) => ({ label, isOut: !isAvailable })),
  }));
};

// Variants sell as flat combinations ("S / Oatmeal"), so the chip row carries one heading
// naming every option it spans — "Size & Color" for a size+color product.
export const buildVariantOptionsLabel = (variants: IMarketplaceVariant[]): string =>
  buildVariantOptionRows(variants)
    .map(({ name }) => name)
    .join(' & ');

export const buildVariantLabel = (variant: IMarketplaceVariant): string => {
  if (!variant.options) {
    return variant.name;
  }

  return Object.entries(variant.options)
    .map(([name, value]) => `${name}: ${value}`)
    .join(' · ');
};

export const isVariantSelectable = (variant: IMarketplaceVariant): boolean =>
  variant.inPool && (!variant.drawsFromWallet || (typeof variant.creditPrice === 'number' && variant.creditPrice > 0));

export const isVariantPickable = (variant: IMarketplaceVariant): boolean =>
  variant.preselected || isVariantSelectable(variant);

export const getSelectableVariants = (details: IMarketplaceProductDetails): IMarketplaceVariant[] =>
  (details.variants ?? []).filter(isVariantSelectable);

export const getPickableVariants = (details: IMarketplaceProductDetails): IMarketplaceVariant[] =>
  (details.variants ?? []).filter(isVariantPickable);

export const hasSingleVariant = (details: IMarketplaceProductDetails): boolean =>
  getSelectableVariants(details).length === 1;

export const buildCartLineFromDetails = (
  details: IMarketplaceProductDetails,
  variant: IMarketplaceVariant,
  catalogItem?: IMarketplaceCatalogItem,
): IMarketplaceCartLine => ({
  product_id: details.product_id,
  variant_id: variant.variant_id,
  creditPrice: variant.creditPrice ?? details.creditPrice ?? 0,
  quantity: 1,
  name: details.name,
  vendor: details.vendor ?? catalogItem?.vendor,
  supplierCode: details.supplierCode ?? catalogItem?.supplierCode,
  supplierName: details.supplierName ?? catalogItem?.supplierName,
  image_url: variant.image_url ?? details.images?.[0],
});

// Bare product ids for every marketplace item in a list, the campaign id included when given.
export const collectMarketplaceProductIds = (items: ICampaignItem[], campaignId?: string): string[] =>
  Array.from(
    new Set([campaignId ?? '', ...items.map((item) => item.id)].filter(isMarketplaceId).map(stripMarketplacePrefix)),
  );

export const getCampaignMarketplaceProductIds = (campaign: ICampaign): string[] =>
  collectMarketplaceProductIds(campaign.items ?? [], campaign.id);

export const isLoneMarketplaceCampaign = (campaign: Pick<ICampaign, 'id' | 'items'>): boolean =>
  isMarketplaceId(campaign.id) && (campaign.items ?? []).length === 1;

export const collectVendorNames = (items: ICampaignItem[], vendorOf: (itemId: string) => string | undefined) => {
  const names = items
    .map((item) => vendorOf(getMarketplaceItemId(item))?.trim())
    .filter((name): name is string => !!name)
    .map(stripTrailingPeriod);

  return Array.from(new Set(names));
};

// Budget claims carry their selection in the marketplace cart rather than in picked_items.
// Mapping each line to ICampaignItem lets Card, SummaryCard and the details drawer render a cart
// the way they render a picked campaign; the chosen variant travels in variantName.
export const mapCartLinesToCampaignItems = (
  cart: IMarketplaceCartLine[],
  descriptionOf?: (productId: string) => string | undefined,
): ICampaignItem[] =>
  cart.map((line) => ({
    id: addMarketplacePrefix(line.product_id),
    internalId: line.variant_id,
    name: line.name,
    vendor: line.vendor,
    description: descriptionOf?.(line.product_id),
    image_url: line.image_url ?? '',
    quantity: line.quantity,
    variantName: line.variantName,
    preorder_allowed: false,
    customization: {},
  }));

// Idle counts as pending: on a direct load the cards render before the hydration saga has
// stamped its statuses, and a bare card there would look settled.
export const isDetailsPending = (status: MarketplaceDetailsStatusEnum): boolean =>
  status === MarketplaceDetailsStatusEnum.Loading || status === MarketplaceDetailsStatusEnum.Idle;

export const isDetailsSettled = (status: MarketplaceDetailsStatusEnum): boolean =>
  status === MarketplaceDetailsStatusEnum.Loaded || status === MarketplaceDetailsStatusEnum.Failed;

// A resolved item with a single main variant has nothing to choose and moves to Included items.
export const isSettledWithoutOptions = (item: ICampaignItem, isSettled: boolean): boolean =>
  isSettled && !(item.sku_options && item.sku_options.length > 0);

export const areAllVariantsOutOfStock = (details: IMarketplaceProductDetails): boolean => {
  const pickable = getPickableVariants(details);

  return pickable.length > 0 && pickable.every((variant) => variant.isOutOfStock);
};

export const needsOptionsStep = (details: IMarketplaceProductDetails): boolean =>
  areAllVariantsOutOfStock(details) || getPickableVariants(details).length > 1;

// The first pick locks the cart to its supplier. A pick with no code yields the empty-code lock.
// The claim-page endpoints send no supplier name, so the picked item's brand names the lock.
export const getSupplierLock = (cart: IMarketplaceCartLine[]): ISupplierLock | null => {
  const [first] = cart;

  if (!first) {
    return null;
  }

  return { code: first.supplierCode ? String(first.supplierCode) : '', name: first.supplierName || first.vendor };
};

// A missing code never matches, not even another missing one: the backend rejects such a mix.
export const isOtherSupplier = (supplierCode: string | undefined, lock: ISupplierLock | null): boolean => {
  if (!lock) {
    return false;
  }

  return !supplierCode || !lock.code || String(supplierCode) !== lock.code;
};

const normalizeName = (value?: string): string => (value ?? '').trim().toLowerCase();

export const isShippedByPartner = (vendor?: string, supplierName?: string): boolean =>
  !!normalizeName(supplierName) && normalizeName(supplierName) !== normalizeName(vendor);
